import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { CreditFooter } from "@/components/CreditFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Wand2,
  Sparkles,
  Copy,
  Target,
  Zap,
  Globe,
  Brain,
  AlertTriangle,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const NONE = "__none__";

export default function SmartHooks() {
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(NONE);

  const loadUsage = () => api.get("/hooks/usage").then((r) => {
    setUsage(r.data);
    setLimitReached(r.data.remaining <= 0);
  });

  useEffect(() => {
    loadUsage();
    api.get("/offer-profiles").then((r) => {
      const list = r.data.profiles || [];
      setProfiles(list);
      const def = list.find((p) => p.is_default);
      if (def) selectProfile(def.id, list);
    }).catch(() => {});
  }, []);

  const selectProfile = (id, list = profiles) => {
    setProfileId(id);
    if (id === NONE) return;
    const p = list.find((x) => x.id === id);
    if (p) {
      setTopic(p.selling || "");
      setAudience(p.audience || "");
    }
  };

  const generate = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/hooks/generate", {
        topic, audience,
        offer_profile_id: profileId === NONE ? "" : profileId,
      });
      setResult(data);
      setUsage({ used: data.limit - data.remaining, remaining: data.remaining, limit: data.limit });
      setLimitReached(data.remaining <= 0);
      toast.success("Your million-dollar hooks are ready!");
    } catch (err) {
      if (err.response?.status === 429) {
        setLimitReached(true);
        toast.error("Daily limit reached");
      } else {
        toast.error(err.response?.data?.detail || "Generation failed");
      }
    } finally {
      setLoading(false);
      loadUsage();
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove();
    }
    toast.success("Copied!");
  };

  const sourceMeta = {
    web_search: { icon: Globe, label: "Researched via live web search" },
    ai_fallback: { icon: Brain, label: "AI-inferred (web search unavailable)" },
    ai: { icon: Brain, label: "AI-inferred — add a Perplexity key for live web search" },
  };

  return (
    <div className="space-y-8" data-testid="smart-hooks-page">
      <div className="fade-up flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Wand2 size={24} />
          </div>
          <div>
            <h1 className="font-head text-4xl font-bold">Smart Hook Generator</h1>
            <p className="text-muted-foreground">
              We research real audience pain points, then a direct-response copywriter turns them into scroll-stopping hooks.
            </p>
          </div>
        </div>
        {usage && (
          <span
            data-testid="hook-usage"
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
              usage.remaining > 0 ? "bg-accent/15 text-accent" : "bg-destructive/15 text-destructive"
            }`}
          >
            {usage.remaining}/{usage.limit} left today
          </span>
        )}
      </div>

      <Card className="border-border p-8">
        {profiles.length > 0 && (
          <div className="mb-6 space-y-2">
            <Label>Base it on an Offer Profile</Label>
            <Select value={profileId} onValueChange={(v) => selectProfile(v)}>
              <SelectTrigger data-testid="hooks-profile-select" className="w-full sm:max-w-md">
                <SelectValue placeholder="Choose an offer profile" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE} data-testid="hooks-profile-none">None — I'll type my own</SelectItem>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`hooks-profile-${p.id}`}>
                    {p.name}{p.is_default ? " (default)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Prefills the topic & audience and personalizes hooks to that offer. Edit anything below.
            </p>
          </div>
        )}
        <div className="grid gap-6 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>Topic or offer</Label>
            <Input data-testid="hook-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
              placeholder="e.g. Managing irregular freelance income" />
          </div>
          <div className="space-y-2">
            <Label>Audience (optional)</Label>
            <Input data-testid="hook-audience" value={audience} onChange={(e) => setAudience(e.target.value)}
              placeholder="e.g. New freelancers" />
          </div>
        </div>
        <div className="mt-6 flex justify-end">
          <Button onClick={generate} disabled={loading || limitReached || topic.trim().length < 3}
            data-testid="generate-hooks-btn"
            className="rounded-full bg-primary px-8 py-6 text-base font-semibold hover:bg-primary/90">
            {loading ? (
              <><RefreshCw size={18} className="mr-2 animate-spin" /> Researching & writing...</>
            ) : (
              <><Sparkles size={18} className="mr-2" /> Generate Hooks</>
            )}
          </Button>
        </div>
      </Card>

      {limitReached && !result && (
        <Card className="flex items-center gap-4 border-destructive/30 bg-destructive/5 p-6" data-testid="limit-reached">
          <AlertTriangle className="text-destructive" size={24} />
          <div>
            <p className="font-head text-lg font-semibold text-destructive">Limit Reached</p>
            <p className="text-sm text-muted-foreground">
              You've used all {usage?.limit || 3} Smart Hook generations for the last 24 hours. Please try again later.
            </p>
          </div>
        </Card>
      )}

      {loading && (
        <Card className="flex flex-col items-center gap-3 border-dashed p-14 text-center">
          <RefreshCw size={26} className="animate-spin text-primary" />
          <p className="text-muted-foreground">Searching the web for pain points and crafting your hooks…</p>
        </Card>
      )}

      {result && (
        <div className="space-y-8 fade-up">
          {result.search_source && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="search-source">
              {(() => { const M = sourceMeta[result.search_source]; const I = M?.icon || Globe; return <I size={15} />; })()}
              {sourceMeta[result.search_source]?.label}
            </div>
          )}

          <div>
            <h2 className="mb-4 flex items-center gap-2 font-head text-2xl font-bold">
              <Target size={20} className="text-primary" /> Top pain points
            </h2>
            <div className="grid gap-3 md:grid-cols-3">
              {result.pain_points.map((p, i) => (
                <Card key={`${p.pain_point || "pain"}-${i}`} className="border-border p-5" data-testid={`pain-point-${i}`}>
                  <p className="font-head font-semibold">{p.pain_point}</p>
                  {p.evidence && <p className="mt-2 text-sm text-muted-foreground">{p.evidence}</p>}
                </Card>
              ))}
            </div>
          </div>

          <div>
            <h2 className="mb-4 flex items-center gap-2 font-head text-2xl font-bold">
              <Zap size={20} className="text-primary" /> Million-dollar hooks
            </h2>
            <div className="space-y-3">
              {result.hooks.map((h, i) => (
                <Card key={`${h.hook || "hook"}-${i}`} className="group border-border p-6 transition-transform duration-200 hover:-translate-y-0.5" data-testid={`hook-${i}`}>
                  <div className="flex items-start justify-between gap-4">
                    <p className="font-head text-lg font-semibold leading-snug">"{h.hook}"</p>
                    <button onClick={() => copy(h.hook)} data-testid={`copy-hook-${i}`} title="Copy hook"
                      className="shrink-0 rounded-full border border-border p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-primary">
                      <Copy size={15} />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {h.angle && <span className="rounded-full bg-secondary px-3 py-0.5 text-xs font-medium">{h.angle}</span>}
                    {h.addresses && <span className="rounded-full bg-primary/10 px-3 py-0.5 text-xs font-medium text-primary">Targets: {h.addresses}</span>}
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {result.citations?.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Sources</p>
              <div className="space-y-1">
                {result.citations.slice(0, 6).map((c, i) => {
                  const url = typeof c === "string" ? c : c?.url;
                  return url ? (
                    <a key={url} href={url} target="_blank" rel="noopener noreferrer"
                      className="block truncate text-sm text-blue-600 hover:underline">{url}</a>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      )}
      <CreditFooter className="mt-8" />
    </div>
  );
}
